import { Controller } from '@hotwired/stimulus'
import mapboxgl from 'mapbox-gl'
import { createMap, teardownMap } from '../utils/ladot_map'
import { hubMapViewForRadius } from '../utils/imh_map_view.mjs'
import { revealSelectedListItem } from '../utils/imh_stop_list_navigation.mjs'
import {
  addCandidateToDraft,
  candidateRowWithDraftMapping,
  groupColorForKey,
  groupKeyForSelection,
  groupMappings,
  mappingKey,
  readOnlyMapMarkers,
  refreshPreviewIfOpen,
  replaceDraftFromResponse,
  selectionStateAfterDraftMutation,
  statusForStop,
  ungroupMappings,
  unresolvedRowsForView
} from '../utils/imh_mapping_draft.mjs'

// Display-only: rounded to the nearest 10 ft
const metersToFeet = (meters) => Math.round((meters * 3.28084) / 10) * 10

const STATUS_LABELS = {
  standalone: 'Standalone',
  grouped: 'Grouped',
  unmapped: 'Unmapped',
  'mapped-to-other-hub': 'Mapped to another hub',
  unresolved: 'Unresolved'
}
const STATUS_MARKS = {
  standalone: 'S',
  grouped: 'G',
  unmapped: '+',
  'mapped-to-other-hub': '↗',
  unresolved: '?'
}
const STATUS_COLORS = {
  standalone: '#2563eb',
  grouped: '#7c3aed',
  unmapped: '#15803d',
  'mapped-to-other-hub': '#b45309',
  unresolved: '#b91c1c'
}
const FEED_LABELS = {
  ladot_transit_gtfs: 'LADOT',
  lacmta_bus_gtfs: 'LACMTA bus',
  lacmta_rail_gtfs: 'LACMTA Rail'
}

export default class extends Controller {
  static targets = [
    'map', 'mapError', 'stopList', 'selectedDetail', 'unresolvedList', 'searchInput',
    'radiusOption', 'groupNameInput', 'groupSelect', 'status', 'preview',
    'previewButton', 'previewContent', 'saveButton', 'reloadButton'
  ]

  static values = {
    readOnly: Boolean,
    latitude: Number,
    longitude: Number,
    stopDetails: Array,
    draftUrl: String,
    candidatesUrl: String,
    saveUrl: String
  }

  connect () {
    this.stopDetails = this.stopDetailsValue || []
    this.candidates = []
    this.selectedStopKey = null
    this.selectedMarkerKey = null
    this.selectedMappingKeys = new Set()
    this.mapMarkers = new Map()
    this.candidateRequestId = 0
    this.draft = null
    this.originalDraft = null
    this.stale = false
    this.mapLoaded = false

    this.initializeMap()
    this.renderStopList()
    this.renderUnresolvedList()

    if (!this.readOnlyValue) this.loadDraft()
  }

  disconnect () {
    this.mapMarkers.forEach((marker) => marker.remove())
    this.mapMarkers.clear()
    teardownMap(this.map)
    this.map = null
  }

  async loadDraft () {
    this.setStatus('Loading complete draft…')
    this.reloadButtonTarget.hidden = true
    this.saveButtonTarget.disabled = false

    try {
      const response = await fetch(this.draftUrlValue, { headers: { Accept: 'application/json' } })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || 'Could not load the current draft.')

      this.draft = replaceDraftFromResponse(body)
      this.originalDraft = replaceDraftFromResponse(body)
      this.renderGroupSelect()
      this.renderStopList()
      this.renderUnresolvedList()
      await this.loadCandidates()
      this.refreshOpenPreview()
      this.setStatus(`Draft loaded. ${this.draft.mappings.length} mapping${this.draft.mappings.length === 1 ? '' : 's'} currently saved.`)
    } catch (error) {
      this.setStatus(error.message || 'Could not load the current draft.')
    }
  }

  selectedRadiusMeters () {
    const checkedRadius = Number(this.radiusOptionTargets.find((target) => target.checked)?.value)
    return checkedRadius === 200 ? 200 : 75
  }

  updateMapForRadius (radius, { animate = true } = {}) {
    if (!this.map) return

    const mapView = hubMapViewForRadius(radius, this.longitudeValue, this.latitudeValue)
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false
    if (!animate || !this.mapLoaded || prefersReducedMotion) {
      this.map.jumpTo(mapView)
    } else {
      this.map.easeTo({ ...mapView, duration: 350 })
    }
  }

  async loadCandidates () {
    if (this.readOnlyValue || !this.draft) return

    const requestId = ++this.candidateRequestId
    const radius = this.selectedRadiusMeters()
    this.updateMapForRadius(radius)
    const params = new URLSearchParams({
      radius_meters: radius,
      format: 'json'
    })
    this.setStatus(`Loading nearby candidates from all transit feeds within ${radius} m (${metersToFeet(radius)} ft)…`)

    try {
      const response = await fetch(`${this.candidatesUrlValue}?${params}`, { headers: { Accept: 'application/json' } })
      const body = await response.json()
      if (requestId !== this.candidateRequestId) return
      if (!response.ok) throw new Error(body.error || 'Could not load candidate stops.')

      this.candidates = body.candidates || []
      this.reconcileSelection()
      this.renderStopList()
      this.renderUnresolvedList()
      this.renderMapMarkers()
      const feeds = body.feeds || []
      const availableFeeds = feeds.filter((feed) => feed.available)
        .map((feed) => FEED_LABELS[feed.feed_key] || feed.feed_key)
      const unavailableFeeds = feeds.filter((feed) => !feed.available)
        .map((feed) => FEED_LABELS[feed.feed_key] || feed.feed_key)
      if (availableFeeds.length === 0) {
        this.setStatus('No current published transit feeds are available.')
      } else {
        const summary = `${this.candidates.length} nearby candidate stop${this.candidates.length === 1 ? '' : 's'} loaded from ${availableFeeds.join(', ')}.`
        this.setStatus(unavailableFeeds.length ? `${summary} Unavailable: ${unavailableFeeds.join(', ')}.` : summary)
      }
    } catch (error) {
      if (requestId !== this.candidateRequestId) return
      this.candidates = []
      this.reconcileSelection()
      this.renderStopList()
      this.renderMapMarkers()
      this.setStatus(error.message || 'Could not load candidate stops.')
    }
  }

  initializeMap () {
    try {
      const initialView = hubMapViewForRadius(this.selectedRadiusMeters(), this.longitudeValue, this.latitudeValue)
      this.map = createMap({
        container: this.mapTarget,
        accessToken: window.mapbox_data?.access_token,
        center: initialView.center,
        zoom: initialView.zoom
      })
      this.map.once('load', () => {
        this.mapLoaded = true
        this.updateMapForRadius(this.selectedRadiusMeters(), { animate: false })
        this.addHubMarker()
        this.renderMapMarkers()
      })
      this.map.on('error', () => this.showMapError())
    } catch (_) {
      this.showMapError()
    }
  }

  addHubMarker () {
    if (!this.map) return

    const markerElement = document.createElement('div')
    markerElement.className = 'imh-hub-map-marker'
    markerElement.textContent = 'Hub'
    markerElement.setAttribute('aria-label', `Hub location at ${this.latitudeValue}, ${this.longitudeValue}`)
    markerElement.title = 'Hub location'
    markerElement.style.cssText = 'background:#111827;color:white;border-radius:9999px;padding:5px 8px;font-weight:700;font-size:12px;border:2px solid white;box-shadow:0 1px 4px #0008'
    this.hubMarker = new mapboxgl.Marker({ element: markerElement })
      .setLngLat([this.longitudeValue, this.latitudeValue])
      .addTo(this.map)
  }

  searchStops () {
    this.reconcileSelection()
    this.renderStopList()
    this.renderMapMarkers()
  }

  reconcileSelection () {
    if (!this.selectedStopKey) return

    const selection = selectionStateAfterDraftMutation(this.selectedStopKey, this.visibleRows())
    if (selection.selectedRow) {
      this.selectStop(selection.selectedRow)
      return
    }

    this.clearSelection()
  }

  clearSelection () {
    this.selectedStopKey = null
    this.selectedMarkerKey = null
    this.updateSelectedMarker()
    if (!this.hasSelectedDetailTarget) return

    this.selectedDetailTarget.textContent = 'Select a stop on the map or in the list to view its details.'
  }

  renderStopList () {
    this.renderUnresolvedList()
    if (!this.hasStopListTarget) return
    this.stopListTarget.replaceChildren()

    const rows = this.visibleRows()
    if (rows.length === 0) {
      const item = document.createElement('li')
      item.className = 'p-3 text-sm text-secondary'
      item.textContent = this.searchQuery() ? 'No stops match this search.' : 'No transit stops are available in this list.'
      this.stopListTarget.appendChild(item)
      this.renderGroupSelect()
      return
    }

    rows.forEach((row) => this.stopListTarget.appendChild(this.buildStopRow(row)))
    this.renderGroupSelect()
  }

  buildStopRow (row) {
    const item = document.createElement('li')
    item.className = 'p-3 space-y-1'
    const status = this.statusForRow(row)
    const heading = document.createElement('div')
    heading.className = 'flex items-start gap-2'

    if (!this.readOnlyValue && row.mapping) {
      const checkbox = document.createElement('input')
      checkbox.type = 'checkbox'
      checkbox.checked = this.selectedMappingKeys.has(mappingKey(row.mapping))
      checkbox.setAttribute('aria-label', `Select ${this.stopName(row)} for group controls`)
      checkbox.addEventListener('change', () => this.toggleMappingSelection(row.mapping, checkbox.checked))
      heading.appendChild(checkbox)
    }

    const selectButton = document.createElement('button')
    selectButton.type = 'button'
    selectButton.className = 'font-semibold text-left underline-offset-2 hover:underline'
    selectButton.textContent = this.stopName(row)
    selectButton.dataset.stopKey = this.domKey(row)
    selectButton.setAttribute('aria-pressed', (this.mappingKeyForRow(row) === this.selectedStopKey).toString())
    selectButton.addEventListener('click', () => this.selectStop(row))
    heading.appendChild(selectButton)

    const statusBadge = document.createElement('span')
    statusBadge.className = 'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold'
    const groupKey = row.mapping?.group_key || row.group_key
    statusBadge.style.color = groupColorForKey(groupKey) || STATUS_COLORS[status]
    statusBadge.textContent = `${STATUS_MARKS[status]} ${STATUS_LABELS[status]}`
    heading.appendChild(statusBadge)
    item.appendChild(heading)

    const metadata = document.createElement('p')
    metadata.className = 'text-xs text-secondary'
    const identifier = row.external_id || row.stop_external_id || row.legacy_stop_code || 'Stop ID unavailable'
    metadata.textContent = `${FEED_LABELS[row.feed_key] || row.feed_key} · Stop ID: ${identifier}`
    if (row.code) metadata.textContent += ` · Code: ${row.code}`
    if (Number.isFinite(Number(row.distance_meters))) metadata.textContent += ` · ${Math.round(Number(row.distance_meters))} m (${metersToFeet(Number(row.distance_meters))} ft) from hub`
    item.appendChild(metadata)

    if (row.group_name) {
      const group = document.createElement('p')
      group.className = 'text-xs'
      group.style.color = groupColorForKey(groupKey)
      group.textContent = `Group: ${row.group_name}${row.anchor ? ' · Anchor' : ''}`
      item.appendChild(group)
    }

    if (row.other_hubs?.length) {
      const otherHubs = document.createElement('p')
      otherHubs.className = 'text-xs text-amber-800'
      otherHubs.textContent = `Also mapped to ${row.other_hubs.map((hub) => hub.name).join(', ')}. Cross-hub duplicates are allowed.`
      item.appendChild(otherHubs)
    }

    if (!this.readOnlyValue && row.candidate && !row.mapping) {
      const addButton = document.createElement('button')
      addButton.type = 'button'
      addButton.className = 'text-sm font-semibold underline'
      addButton.textContent = status === 'mapped-to-other-hub' ? 'Add to this hub anyway' : 'Add as standalone'
      addButton.setAttribute('aria-label', `${addButton.textContent}: ${this.stopName(row)}`)
      addButton.addEventListener('click', () => this.addCandidate(row))
      item.appendChild(addButton)
    }

    if (!this.readOnlyValue && row.mapping) {
      const actions = document.createElement('div')
      actions.className = 'flex flex-wrap gap-3 text-sm'
      if (row.mapping.group_key) {
        const anchorButton = document.createElement('button')
        anchorButton.type = 'button'
        anchorButton.className = 'underline'
        anchorButton.textContent = row.mapping.anchor ? 'Current group anchor' : 'Set as group anchor'
        anchorButton.setAttribute('aria-label', `${anchorButton.textContent}: ${this.stopName(row)}`)
        anchorButton.disabled = row.mapping.anchor === true
        anchorButton.addEventListener('click', () => this.setAnchor(row.mapping))
        actions.appendChild(anchorButton)
      }

      const removeButton = document.createElement('button')
      removeButton.type = 'button'
      removeButton.className = 'underline text-red-700'
      removeButton.textContent = 'Remove from this hub draft'
      removeButton.setAttribute('aria-label', `Remove ${this.stopName(row)} from this hub draft`)
      removeButton.addEventListener('click', () => this.removeMapping(row.mapping))
      actions.appendChild(removeButton)
      item.appendChild(actions)
    }

    return item
  }

  allRows () {
    const candidates = this.candidates.map((candidate) => {
      const mapping = this.draft?.mappings.find((item) => mappingKey(item) === mappingKey(candidate))
      const detail = this.stopDetails.find((item) => mappingKey(item) === mappingKey(candidate))
      return candidateRowWithDraftMapping(candidate, detail, mapping)
    })
    const candidateKeys = new Set(candidates.map((candidate) => mappingKey(candidate)))
    const draftRows = (this.draft?.mappings || [])
      .filter((mapping) => !candidateKeys.has(mappingKey(mapping)))
      .map((mapping) => {
        const detail = this.stopDetails.find((item) => mappingKey(item) === mappingKey(mapping)) || {}
        return {
          ...detail,
          ...mapping,
          external_id: mapping.stop_external_id,
          mapping,
          candidate: false,
          unresolved: Boolean(detail.unresolved || mapping.unresolved)
        }
      })

    return this.readOnlyValue
      ? this.stopDetails.map((detail) => ({ ...detail, external_id: detail.stop_external_id, mapping: detail, candidate: false }))
      : [...candidates, ...draftRows]
  }

  visibleRows () {
    const rows = this.allRows()
    const query = this.searchQuery()
    const visible = query
      ? rows.filter((row) => [row.name, row.external_id, row.stop_external_id, row.code, row.legacy_stop_code, row.feed_key]
          .some((value) => value?.toLowerCase().includes(query)))
      : rows

    return visible.sort((left, right) => this.stopName(left).localeCompare(this.stopName(right)))
  }

  statusForRow (row) {
    if (row.unresolved && !row.candidate) return 'unresolved'
    if (this.readOnlyValue) return row.group_key ? 'grouped' : 'standalone'
    return statusForStop(row, this.draft?.mappings || [])
  }

  renderUnresolvedList () {
    if (!this.hasUnresolvedListTarget) return

    const unresolved = unresolvedRowsForView({
      readOnly: this.readOnlyValue,
      stopDetails: this.stopDetails,
      draft: this.draft
    })
    this.unresolvedListTarget.replaceChildren()
    if (unresolved.length === 0) {
      const empty = document.createElement('li')
      empty.textContent = 'None.'
      this.unresolvedListTarget.appendChild(empty)
      return
    }

    unresolved.forEach((row) => {
      const item = document.createElement('li')
      const identifier = row.stop_external_id || row.legacy_stop_code || 'Stop ID unavailable'
      item.textContent = `${FEED_LABELS[row.feed_key] || row.feed_key}: ${identifier}`
      this.unresolvedListTarget.appendChild(item)
    })
  }

  renderGroupSelect () {
    if (!this.hasGroupSelectTarget || !this.draft) return

    const selectedValue = this.groupSelectTarget.value
    const groups = new Map()
    this.draft.mappings.forEach((mapping) => {
      if (mapping.group_key) groups.set(mapping.group_key, mapping.group_name)
    })
    this.groupSelectTarget.replaceChildren()
    const newOption = document.createElement('option')
    newOption.value = ''
    newOption.textContent = 'Create a new group'
    this.groupSelectTarget.appendChild(newOption)
    groups.forEach((name, key) => {
      const option = document.createElement('option')
      option.value = key
      option.textContent = name || 'Unnamed group'
      this.groupSelectTarget.appendChild(option)
    })
    if (groups.has(selectedValue)) this.groupSelectTarget.value = selectedValue
  }

  selectGroup () {
    const selectedGroup = this.draft?.mappings.find((mapping) => mapping.group_key === this.groupSelectTarget.value)
    this.groupNameInputTarget.value = selectedGroup?.group_name || ''
  }

  renderMapMarkers () {
    if (!this.mapLoaded || !this.map) return

    this.mapMarkers.forEach((marker) => marker.remove())
    this.mapMarkers.clear()
    const rows = this.visibleRows()
    const mapRows = this.readOnlyValue ? readOnlyMapMarkers(rows) : rows

    mapRows.forEach((row) => {
      if (!this.validCoordinates(row.longitude, row.latitude)) return
      const key = this.mapMarkerKeyForRow(row)
      const status = this.statusForRow(row)
      const markerElement = document.createElement('button')
      markerElement.type = 'button'
      markerElement.className = 'imh-stop-map-marker'
      markerElement.textContent = STATUS_MARKS[status]
      markerElement.title = `${this.stopName(row)} — ${STATUS_LABELS[status]}`
      markerElement.setAttribute('aria-label', `Select ${this.stopName(row)}. Status: ${STATUS_LABELS[status]}. ${row.latitude}, ${row.longitude}`)
      markerElement.setAttribute('aria-pressed', (key === this.selectedMarkerKey).toString())
      markerElement.style.cssText = `width:30px;height:30px;border-radius:9999px;border:2px solid white;background:${groupColorForKey(row.group_key) || STATUS_COLORS[status]};color:white;font-weight:800;box-shadow:0 1px 4px #0008;cursor:pointer`
      markerElement.addEventListener('click', () => {
        const selectedRow = row.kind === 'group' ? row.representative : row
        this.selectStop(selectedRow, key)
        new mapboxgl.Popup({ offset: 14 })
          .setLngLat([Number(row.longitude), Number(row.latitude)])
          .setText(`${this.stopName(row)} — ${STATUS_LABELS[status]}`)
          .addTo(this.map)
      })
      const marker = new mapboxgl.Marker({ element: markerElement })
        .setLngLat([Number(row.longitude), Number(row.latitude)])
        .addTo(this.map)
      this.mapMarkers.set(key, marker)
    })
  }

  selectStop (row, markerKey = this.mapMarkerKeyForRow(row)) {
    this.selectedStopKey = this.mappingKeyForRow(row)
    this.selectedMarkerKey = markerKey
    this.updateSelectedMarker()
    this.renderSelectedDetail(row)
    const selectedButton = Array.from(this.stopListTarget.querySelectorAll('[data-stop-key]')).find((button) => {
      const selected = button.dataset.stopKey === this.domKey(row)
      button.setAttribute('aria-pressed', selected.toString())
      return selected
    })
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false
    revealSelectedListItem(selectedButton, { prefersReducedMotion })
  }

  updateSelectedMarker () {
    this.mapMarkers.forEach((marker, key) => {
      const button = marker.getElement()
      button.setAttribute('aria-pressed', (key === this.selectedMarkerKey).toString())
    })
  }

  renderSelectedDetail (row) {
    this.selectedDetailTarget.replaceChildren()
    const heading = document.createElement('h5')
    heading.className = 'font-semibold'
    heading.textContent = this.stopName(row)
    this.selectedDetailTarget.appendChild(heading)

    const status = document.createElement('p')
    status.textContent = `Status: ${STATUS_LABELS[this.statusForRow(row)]}`
    this.selectedDetailTarget.appendChild(status)

    const identity = document.createElement('p')
    identity.textContent = `Feed: ${FEED_LABELS[row.feed_key] || row.feed_key} · Stop ID: ${row.external_id || row.stop_external_id || row.legacy_stop_code || 'Unavailable'}`
    this.selectedDetailTarget.appendChild(identity)
    if (row.code) {
      const code = document.createElement('p')
      code.textContent = `Stop code: ${row.code}`
      this.selectedDetailTarget.appendChild(code)
    }
    if (this.validCoordinates(row.longitude, row.latitude)) {
      const coordinates = document.createElement('p')
      coordinates.textContent = `Coordinates: ${Number(row.latitude).toFixed(6)}, ${Number(row.longitude).toFixed(6)}`
      this.selectedDetailTarget.appendChild(coordinates)
    }

    const groupKey = row.mapping?.group_key || row.group_key
    if (groupKey) {
      const members = (this.draft?.mappings || this.stopDetails).filter((mapping) => mapping.group_key === groupKey)
      const memberList = document.createElement('ul')
      memberList.className = 'list-disc pl-5 mt-1'
      members.forEach((member) => {
        const item = document.createElement('li')
        const detail = this.stopDetails.find((stop) => mappingKey(stop) === mappingKey(member)) || {}
        const candidate = this.candidates.find((stop) => mappingKey(stop) === mappingKey(member))
        const memberRow = {
          ...detail,
          ...candidate,
          ...member,
          external_id: member.stop_external_id,
          mapping: member,
          candidate: Boolean(candidate)
        }
        const memberButton = document.createElement('button')
        memberButton.type = 'button'
        memberButton.className = 'underline text-left'
        memberButton.textContent = `${FEED_LABELS[member.feed_key] || member.feed_key}: ${this.stopName(memberRow)}${member.anchor ? ' (anchor)' : ''}`
        memberButton.setAttribute('aria-label', `View ${this.stopName(memberRow)} member details${member.anchor ? ', group anchor' : ''}`)
        memberButton.addEventListener('click', () => this.selectStop(memberRow))
        item.appendChild(memberButton)
        memberList.appendChild(item)
      })
      const label = document.createElement('p')
      label.textContent = `Group members (${row.group_name || row.mapping?.group_name || 'unnamed group'})`
      this.selectedDetailTarget.append(label, memberList)
    }

    if (row.other_hubs?.length) {
      const otherHubText = document.createElement('p')
      otherHubText.textContent = `Also mapped to ${row.other_hubs.map((hub) => hub.name).join(', ')}. Cross-hub duplicates are allowed.`
      this.selectedDetailTarget.appendChild(otherHubText)
    }
  }

  addCandidate (candidate) {
    this.draft = addCandidateToDraft(this.draft, candidate)
    this.reconcileSelection()
    this.renderGroupSelect()
    this.renderStopList()
    this.renderMapMarkers()
    this.refreshOpenPreview()
    this.setStatus(`${this.stopName(candidate)} added as a standalone stop. Save the draft to apply this change.`)
  }

  toggleMappingSelection (mapping, selected) {
    const key = mappingKey(mapping)
    if (selected) this.selectedMappingKeys.add(key)
    else this.selectedMappingKeys.delete(key)
  }

  groupSelected () {
    const selectedKeys = [...this.selectedMappingKeys]
    if (selectedKeys.length < 2) {
      this.setStatus('Select at least two mapped stops in the stop list before grouping them.')
      return
    }

    const selectedMappings = this.draft.mappings.filter((mapping) => selectedKeys.includes(mappingKey(mapping)))
    const selectedGroupKeys = [...new Set(selectedMappings.map((mapping) => mapping.group_key).filter(Boolean))]
    const requestedGroupKey = this.groupSelectTarget.value
    const groupKey = groupKeyForSelection(this.draft.mappings, selectedKeys, requestedGroupKey, `draft-${window.crypto.randomUUID()}`)
    const existing = this.draft.mappings.find((mapping) => mapping.group_key === groupKey)
    const groupName = this.groupNameInputTarget.value.trim() || existing?.group_name ||
      (selectedGroupKeys.length <= 1 ? selectedMappings.find((mapping) => mapping.group_name)?.group_name : null)
    if (!groupName) {
      this.setStatus('Enter a group name before grouping stops.')
      this.groupNameInputTarget.focus()
      return
    }

    const previousGroups = [...new Set(selectedMappings.map((mapping) => mapping.group_key).filter((key) => key && key !== groupKey))]
    const anchorMapping = selectedMappings.find((mapping) => mapping.group_key === groupKey && mapping.anchor) ||
      this.draft.mappings.find((mapping) => mapping.group_key === groupKey && mapping.anchor) ||
      selectedMappings.find((mapping) => mapping.anchor)
    const anchorKey = anchorMapping ? mappingKey(anchorMapping) : selectedKeys[0]
    this.draft.mappings = groupMappings(this.draft.mappings, selectedKeys, { groupKey, groupName, anchorKey })
      .map((mapping) => mapping.group_key === groupKey ? { ...mapping, group_name: groupName } : mapping)
    previousGroups.forEach((previousGroup) => this.ensureGroupAnchor(previousGroup))
    this.reconcileSelection()
    this.selectedMappingKeys.clear()
    this.groupNameInputTarget.value = groupName
    this.groupSelectTarget.value = groupKey
    this.renderGroupSelect()
    this.renderStopList()
    this.renderMapMarkers()
    this.refreshOpenPreview()
    this.setStatus(`${selectedKeys.length} stops grouped as ${groupName}.`)
  }

  ungroupSelected () {
    const selectedKeys = [...this.selectedMappingKeys]
    if (selectedKeys.length === 0) {
      this.setStatus('Select one or more mapped stops before making them standalone.')
      return
    }

    const previousGroups = [...new Set(this.draft.mappings
      .filter((mapping) => selectedKeys.includes(mappingKey(mapping)) && mapping.group_key)
      .map((mapping) => mapping.group_key))]
    this.draft.mappings = ungroupMappings(this.draft.mappings, selectedKeys)
    previousGroups.forEach((groupKey) => this.ensureGroupAnchor(groupKey))
    this.reconcileSelection()
    this.selectedMappingKeys.clear()
    this.renderGroupSelect()
    this.renderStopList()
    this.renderMapMarkers()
    this.refreshOpenPreview()
    this.setStatus(`${selectedKeys.length} selected stop${selectedKeys.length === 1 ? '' : 's'} will be standalone.`)
  }

  ensureGroupAnchor (groupKey) {
    const members = this.draft.mappings.filter((mapping) => mapping.group_key === groupKey)
    if (members.length === 0 || members.some((mapping) => mapping.anchor)) return

    const anchorKey = mappingKey(members[0])
    this.draft.mappings = this.draft.mappings.map((mapping) => mapping.group_key === groupKey
      ? { ...mapping, anchor: mappingKey(mapping) === anchorKey }
      : mapping)
  }

  setAnchor (mapping) {
    this.draft.mappings = this.draft.mappings.map((row) => row.group_key === mapping.group_key
      ? { ...row, anchor: mappingKey(row) === mappingKey(mapping) }
      : row)
    this.reconcileSelection()
    this.renderStopList()
    this.renderMapMarkers()
    this.refreshOpenPreview()
    this.setStatus(`${this.stopName(mapping)} is now the anchor for ${mapping.group_name}.`)
  }

  removeMapping (mapping) {
    this.draft.mappings = this.draft.mappings.filter((row) => mappingKey(row) !== mappingKey(mapping))
    if (mapping.group_key && mapping.anchor) this.ensureGroupAnchor(mapping.group_key)
    this.selectedMappingKeys.delete(mappingKey(mapping))
    this.reconcileSelection()
    this.renderGroupSelect()
    this.renderStopList()
    this.renderMapMarkers()
    this.refreshOpenPreview()
    this.setStatus(`${mapping.stop_external_id || mapping.legacy_stop_code || 'Stop'} removed from this hub draft. Save the draft to apply this change.`)
  }

  refreshOpenPreview () {
    if (!this.hasPreviewTarget) return

    refreshPreviewIfOpen(this.previewTarget, () => this.renderPreview())
  }

  togglePreview () {
    const show = this.previewTarget.hidden
    this.previewTarget.hidden = !show
    this.previewButtonTarget.setAttribute('aria-expanded', show.toString())
    if (show) this.renderPreview()
  }

  renderPreview () {
    this.previewContentTarget.replaceChildren()
    const original = new Map((this.originalDraft?.mappings || []).map((mapping) => [mappingKey(mapping), mapping]))
    const current = new Map((this.draft?.mappings || []).map((mapping) => [mappingKey(mapping), mapping]))
    const added = [...current.keys()].filter((key) => !original.has(key)).length
    const removed = [...original.keys()].filter((key) => !current.has(key)).length
    const changedGroups = [...current.entries()].filter(([key, mapping]) => {
      const previous = original.get(key)
      return previous && (previous.group_key !== mapping.group_key || previous.group_name !== mapping.group_name || previous.anchor !== mapping.anchor)
    }).length
    const summary = document.createElement('p')
    summary.textContent = `${current.size} total mappings · ${added} added · ${removed} removed · ${changedGroups} group or anchor changes.`
    this.previewContentTarget.appendChild(summary)

    const list = document.createElement('ul')
    list.className = 'list-disc pl-5 mt-2'
    this.allRows().filter((row) => row.mapping).forEach((row) => {
      const item = document.createElement('li')
      const group = row.mapping.group_name ? ` — group ${row.mapping.group_name}${row.mapping.anchor ? ' (anchor)' : ''}` : ' — standalone'
      item.textContent = `${this.stopName(row)} (${FEED_LABELS[row.feed_key] || row.feed_key})${group}${row.unresolved ? ' — unresolved' : ''}`
      list.appendChild(item)
    })
    this.previewContentTarget.appendChild(list)
  }

  async saveDraft () {
    if (!this.draft || this.stale) return

    this.saveButtonTarget.disabled = true
    this.setStatus('Saving complete draft…')
    try {
      const response = await fetch(this.saveUrlValue, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({ draft: this.draft })
      })
      const body = await response.json()
      if (response.status === 409) {
        this.stale = true
        this.saveButtonTarget.disabled = true
        this.reloadButtonTarget.hidden = false
        this.setStatus(body.error || 'This draft is stale. Reload the latest draft before saving.')
        return
      }
      if (!response.ok) throw new Error(body.error || 'Draft could not be saved.')

      this.draft = replaceDraftFromResponse(body)
      this.originalDraft = replaceDraftFromResponse(body)
      this.stale = false
      this.reloadButtonTarget.hidden = true
      this.reconcileSelection()
      this.renderGroupSelect()
      this.renderStopList()
      this.renderUnresolvedList()
      this.renderMapMarkers()
      this.refreshOpenPreview()
      this.setStatus('Draft saved. The server revision and group keys have been refreshed.')
    } catch (error) {
      this.setStatus(error.message || 'Draft could not be saved.')
    } finally {
      if (!this.stale) this.saveButtonTarget.disabled = false
    }
  }

  async reloadDraft () {
    this.stale = false
    this.selectedMappingKeys.clear()
    await this.loadDraft()
  }

  searchQuery () {
    return this.hasSearchInputTarget ? this.searchInputTarget.value.trim().toLowerCase() : ''
  }

  mapMarkerKeyForRow (row) {
    if (this.readOnlyValue && row.group_key) return `group:${row.group_key}`
    return this.mappingKeyForRow(row)
  }

  mappingKeyForRow (row) {
    return mappingKey(row.mapping || row)
  }

  domKey (row) {
    return encodeURIComponent(this.mappingKeyForRow(row))
  }

  stopName (row) {
    return row.name || row.external_id || row.stop_external_id || row.legacy_stop_code || 'Unnamed stop'
  }

  validCoordinates (longitude, latitude) {
    if (longitude === null || longitude === undefined || longitude === '' || latitude === null || latitude === undefined || latitude === '') return false

    const lng = Number(longitude)
    const lat = Number(latitude)
    return Number.isFinite(lng) && lng >= -180 && lng <= 180 && Number.isFinite(lat) && lat >= -90 && lat <= 90
  }

  setStatus (message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  showMapError () {
    if (this.hasMapErrorTarget) this.mapErrorTarget.hidden = false
  }
}
